import { Input, PageHeaderWrapper } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useSetSearchParams } from "@src/appV2/lib/utils/useSetSearchParams";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { ListLoadingState } from "@src/appV2/redesign/components/ListLoadingState";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useEffect, useMemo, useRef } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";

import { DOCUMENTS_BASE_PATH_FULL } from "../paths";
import { DocumentSearchError } from "./Error";
import { DocumentSearchNotFound } from "./NotFound";
import { DocumentSearchResults } from "./Results";
import { useIsWorkerRequirementsSearchEnabled } from "./useIsWorkerRequirementsSearchEnabled";
import { useSearchWorkerRequirements } from "./useSearchWorkerRequirements";

const MAX_SEARCH_LENGTH = 64;
const DEBOUNCE_TIME_IN_MS = 300;

export function DocumentSearchPage() {
  const isWorkerRequirementsSearchEnabled = useIsWorkerRequirementsSearchEnabled();

  const history = useHistory();
  const location = useLocation();

  const { q: incomingSearchTerm = "" } = useSearchParams<{ q?: string }>();
  const setSearchParams = useSetSearchParams();

  const clampedSearchTerm = incomingSearchTerm.slice(0, MAX_SEARCH_LENGTH);
  const debouncedSearchTerm = useDebounce({
    value: clampedSearchTerm,
    debounceTimeInMs: DEBOUNCE_TIME_IN_MS,
  });
  const sanitizedSearchTerm = debouncedSearchTerm.trim();

  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    results,
    isLoading,
    isError,
    error,
    isFetched,
    fetchStatus,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useSearchWorkerRequirements(
    { searchTerm: sanitizedSearchTerm },
    { enabled: isWorkerRequirementsSearchEnabled }
  );

  useLogEffect(APP_V2_USER_EVENTS.DOCUMENT_SEARCH_VIEWED, undefined, {
    enabled: isWorkerRequirementsSearchEnabled,
  });

  const prevFetchStatusRef = useRef(fetchStatus);
  const prevIsFetchingNextPageRef = useRef(isFetchingNextPage);

  useEffect(() => {
    const previousFetchStatus = prevFetchStatusRef.current;
    prevFetchStatusRef.current = fetchStatus;
    const wasFetchingNextPage = prevIsFetchingNextPageRef.current;
    prevIsFetchingNextPageRef.current = isFetchingNextPage;

    // We don't want to log when serving cached results
    const didPreviousFetchComplete = previousFetchStatus === "fetching" && fetchStatus === "idle";
    if (!didPreviousFetchComplete) {
      return;
    }
    if (wasFetchingNextPage) {
      return;
    }
    if (sanitizedSearchTerm === "") {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.DOCUMENT_SEARCH_RESULTS_DISPLAYED, {
      searchTerm: sanitizedSearchTerm,
      loadedResultCount: results.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally excluding results.length from deps to avoid re-triggering on pagination
  }, [fetchStatus, isFetchingNextPage, sanitizedSearchTerm]);

  const content = useMemo(() => {
    if (isLoading) {
      return <ListLoadingState texts={["Searching documents..."]} />;
    }

    if (isError) {
      return <DocumentSearchError error={error} />;
    }

    if (isFetched && results.length > 0) {
      return (
        <DocumentSearchResults
          searchTerm={sanitizedSearchTerm}
          results={results}
          hasNextPage={hasNextPage ?? false}
          isFetchingNextPage={isFetchingNextPage}
          scrollParent={scrollRef.current ?? undefined}
          onEndReached={() => {
            void fetchNextPage();
          }}
        />
      );
    }

    if (isFetched && results.length === 0) {
      return <DocumentSearchNotFound searchTerm={sanitizedSearchTerm} />;
    }

    return null;
  }, [
    isLoading,
    isError,
    error,
    isFetched,
    results,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    sanitizedSearchTerm,
  ]);

  if (!isWorkerRequirementsSearchEnabled) {
    return <Redirect to={DOCUMENTS_BASE_PATH_FULL} />;
  }

  return (
    <PageWrapper>
      <PageHeaderWrapper>
        <Stack
          direction="row"
          alignItems="center"
          spacing={3}
          sx={{ paddingX: 5, paddingTop: 3, paddingBottom: 5 }}
        >
          <Input
            placeholder="Search by document name"
            iconType="search"
            value={clampedSearchTerm}
            inputProps={{ maxLength: MAX_SEARCH_LENGTH }}
            onChange={(event) => {
              setSearchParams({ q: event.target.value }, { shouldReplace: true });
            }}
          />
          <IconButton
            iconType="close"
            variant="outlined"
            aria-label="Close search"
            onClick={() => {
              if (isDefined(location.key)) {
                history.goBack();
              } else {
                history.push(DOCUMENTS_BASE_PATH_FULL);
              }
            }}
          />
        </Stack>
      </PageHeaderWrapper>

      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          paddingX: 5,
          paddingTop: 4,
          paddingBottom: 4,
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {content}
      </Box>
    </PageWrapper>
  );
}
